import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, TextField, Select, MenuItem,
    InputLabel, FormControl, Paper, Container, Snackbar, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Divider,
    Collapse,  Card, CardContent,Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress ,
    DialogContentText,
    DialogActions, Checkbox, Chip, Tooltip,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { createTheme, ThemeProvider, styled,  } from '@mui/material/styles';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MapboxClient from '@mapbox/mapbox-sdk/services/geocoding';

dayjs.extend(utc);
dayjs.extend(timezone);

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
const geocodingClient = MapboxClient({ accessToken: MAPBOX_TOKEN });

const theme = createTheme({
    palette: {
        primary: { main: '#11224d' },
        secondary: { main: '#0e1d3a' },
        warning: { main: '#ffb300' },
        background: { default: '#f9fafc', paper: '#ffffff' },
    },
    typography: { fontFamily: 'Inter, Arial, sans-serif' },
});

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
}));

const formatRelativeTime = (timePosted) => {
    const diffInSec = dayjs().diff(dayjs(timePosted), 'second');
    if (diffInSec < 60) return `${diffInSec} sec`;
    const diffInMin = dayjs().diff(dayjs(timePosted), 'minute');
    if (diffInMin < 60) return `${diffInMin} min`;
    const diffInHr = dayjs().diff(dayjs(timePosted), 'hour');
    if (diffInHr < 24) return `${diffInHr} hr`;
    const diffInDays = dayjs().diff(dayjs(timePosted), 'day');
    return `${diffInDays} d`;
};


const getRegistrationType = (registrationNumber) => {
  if (!registrationNumber) return 'DOT/MC';
  const numStr = String(registrationNumber).toUpperCase();

  if (numStr.includes('MC-') || numStr.startsWith('MC')) {
    return 'MC';
  }
  return 'DOT';
};

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#fdfdfd',
    border: '1px solid #e0e0e0',
    marginBottom: theme.spacing(2),
}));

const CompactTextField = styled(TextField)(() => ({
    '& .MuiInputBase-root': { 
        height: 36, 
        fontSize: '0.85rem' 
    },
    '& .MuiInputLabel-root': { 
        fontSize: '0.7rem',
        
        transform: 'translate(14px, 10px) scale(1)', 
        '&.MuiInputLabel-shrink': {
            
            transform: 'translate(14px, -9px) scale(0.75)', 
        },
    },
}));

const CompactFormControl = styled(FormControl)(() => ({
    minHeight: 36,
    '& .MuiInputLabel-root': { 
        fontSize: '0.75rem',
        
        transform: 'translate(14px, 10px) scale(1)',
        '&.MuiInputLabel-shrink': {
            
            transform: 'translate(14px, -10px) scale(0.75)', 
        },
    },
}));
const CompactSelect = styled(Select)(() => ({
    fontSize: '0.75rem',
    '& .MuiSelect-select': {
        padding: '6px 8px',
        display: 'flex',
        alignItems: 'center',
    },
    height: 36,
}));

const PostButton = styled(Button)(({ theme }) => ({
    borderRadius: 6,
    padding: '6px 16px',
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    '&:disabled': {
        background: '#b0b0b0',
        color: '#fff',
    },
    '&:enabled': {
        background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        color: 'white',
    },
    '&:hover:enabled': {
        background: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
    },
}));

const ClearButton = styled(Button)(({ theme }) => ({
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: 6,
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.05)' },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main,
    '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.08)' },
}));

const TableProgressBar = styled('div')(({ theme }) => ({
  width: '100%',
  height: '4px',
  backgroundColor: '#ffb300',
  overflow: 'hidden',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-30%',
    width: '30%',
    height: '100%',
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(255,255,255,0.6),
        rgba(255,255,255,0.9),
        rgba(255,255,255,0.6),
        transparent
      )
    `,
    animation: 'windowsShimmer 3s linear infinite',
  },

  '@keyframes windowsShimmer': {
    '0%': { left: '-30%' },
    '100%': { left: '100%' },
  },
}));

const StyledTableRow = styled(TableRow)(({ index }) => ({
    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f7f9fc',
}));

const RepostButton = styled(Button)(({ theme, disabled }) => ({
    minWidth: 0, 
    padding: '4px 5px',
    fontSize: '0.6rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.success.main,
    color: 'white',
    '&:hover': {
        backgroundColor: disabled ? theme.palette.grey[400] : theme.palette.success.dark,
    },
}));

const StatusButton = styled(Button)(({ theme, active }) => ({
    minWidth: 0,
    padding: '4px 5px',
    fontSize: '0.6rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    borderRadius: theme.shape.borderRadius,
    marginLeft: '4px',
    backgroundColor: active ? theme.palette.grey[500] : theme.palette.info.main,
    color: 'white',
    '&:hover': {
        backgroundColor: active ? theme.palette.grey[700] : theme.palette.info.dark,
    },
}));

/* MyTrucks.js */

const suggestionsCache = new Map();
let debounceTimer;

const fetchMapboxSuggestions = async (query) => {
    if (!query || query.trim().length < 3) return [];
    const cacheKey = query.trim().toLowerCase();
    if (suggestionsCache.has(cacheKey)) return suggestionsCache.get(cacheKey);

    return new Promise((resolve) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        debounceTimer = setTimeout(async () => {
            try {
                const res = await geocodingClient.forwardGeocode({
                    query,
                    autocomplete: true,
                    limit: 6,
                    countries: ['US'],
                    types: ['place']
                }).send();

                const results = (res.body.features || [])
            .filter(f => !f.place_type.includes('region')) // Ensure no state names
            .map(feature => {
                const city = feature.text || "";
                const stateObj = feature.context?.find(c => c.id.startsWith("region"));
                const state = stateObj?.short_code 
                    ? stateObj.short_code.split("-")[1].toUpperCase() 
                    : (stateObj?.text || "");
                
                return {
                    ...feature,
                    place_name: city && state ? `${city}, ${state}` : city || state
                };
            });
                suggestionsCache.set(cacheKey, results);
                resolve(results);
            } catch (err) {
                console.error('Mapbox Autocomplete error:', err);
                resolve([]);
            }
        }, 300);
    });
};


const TruckRow = ({ truck, idx, handleEdit, handleDelete, handleCopy, handleToggleStatus, handleRepost, equipmentTypes, repostingIds }) => {
    const [open, setOpen] = useState(false);

    const DetailSection = ({ title, children }) => (
        <Box sx={{ pr: 2, mb: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "0.9rem",
                  color: "#11224d",
                  mb: 0.5,
                  borderBottom: '1px solid #ddd',
                  pb: 0.5,
                }}
              >
                {title}
              </Typography>
              {children}
            </Box>
      );

        return (
        <>
            <StyledTableRow index={idx} hover onClick={() => setOpen(!open)} sx={{ cursor: 'pointer' }}>
               
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary', textAlign: 'center' }}>
                    {formatRelativeTime(truck.timePosted)}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                    {dayjs(truck.availableDate).format('MM/DD/YYYY')}
                </TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{truck.origin}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>{truck.destination}</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                {(truck.truckType || truck.equipment || '').split(',').map(e => {
                    const eq = equipmentTypes.find(et => et.label === e.trim());
                    return eq ? eq.short : e;
                }).join(', ')}</TableCell>
             
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center'}}>{truck.companyState || ''}</TableCell>
               
                <TableCell sx={{ fontSize: '0.75rem', textAlign: 'center'}}>
                    <span style={{ 
                        fontWeight: 600, 
                        color: truck.isActive ? '#00c853' : '#ff9800', 
                        fontSize: '0.67rem',
                        padding: '2px 5px',
                        borderRadius: '4px',
                        backgroundColor: truck.isActive ? '#e8f5e9' : '#fff3e0',
                        whiteSpace: 'nowrap'
                    }}>
                        {truck.isActive ? 'ACTIVE' : 'DEACTIVE'}
                    </span>
                </TableCell>
                <TableCell sx={{ borderBottom: open ? 'none' : null,textAlign: 'center',
    whiteSpace: 'nowrap', }} onClick={(e) => e.stopPropagation()}>
                    
                     <StyledIconButton onClick={() => handleEdit(truck.id)} size="small"><EditIcon fontSize="inherit" /></StyledIconButton>
                    <StyledIconButton onClick={() => handleDelete(truck.id)} size="small"><DeleteIcon fontSize="inherit" /></StyledIconButton>
                    <RepostButton
                          
                            onClick={() => handleRepost(truck.id)} 
                            size="small"
                            disabled={repostingIds.has(truck.id)}
                        >
                            {repostingIds.has(truck.id) ? <CircularProgress size={16} color="inherit" /> : 'REPOST'}
                        </RepostButton>
                    <StatusButton
              active={truck.isActive}
              onClick={() => handleToggleStatus(truck.id)}
              size="small"
          >
              {truck.isActive ? 'DEACTIVATE' : 'ACTIVATE'} 
          </StatusButton>

                    
                </TableCell>
            </StyledTableRow>

           
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
            <StyledCard>
              <CardContent>
                                 <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: "bold",
                                                            mb: 1,
                                                            fontSize: "1rem",
                                                            color: "#11224d", 
                                                        }}
                                                    >
                                    {truck.origin} → {truck.destination} — ({truck.companyState})
                                </Typography>
                <Divider />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    mt: 1,
                  }}
                >
                  <Box sx={{ flex: 1, pr: 2 }}>
                    <DetailSection title="Truck Details">
                     
  <Box sx={{ display: "flex", alignItems: "flex-start", mt: 1 }}>
   
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        mr: 1.5,
        mt: 0.5,
      }}
    >
   
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          border: "2px solid #1976d2",
          backgroundColor: "white",
        }}
      />
  
      <Box
        sx={{
          width: 0,
          height: 36,
          borderLeft: "1px dashed #bdbdbd",
        }}
      />
    
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: "#1976d2",
        }}
      />
    </Box>

  
    <Box>
    
      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
        {truck.origin}{" "}
      </Typography>
            <Typography variant="body2" sx={{ ml: 1 }}> 
                {truck.availableDate ? dayjs(truck.availableDate).format('MM/DD/YYYY') : "N/A"}
            </Typography>

   
      <Typography
        variant="body2"
        sx={{ fontWeight: "bold", mt: 1 }}
      >
        {truck.destination}{" "}
      </Typography>
    </Box>
  </Box>
     
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}> 
                          Equipment:
                        </span>{" "}
                        {(truck.truckType || '').split(',').join(', ')}
                      </Typography>
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
                          Load:
                        </span>{" "}
                        {truck.fullOrPartial}
                      </Typography>
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Feet:</span>{" "}
                        {truck.length} ft
                      </Typography>
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Weight:</span>{" "}
                        {truck.weight} lbs
                      </Typography>
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Reference ID:</span>{" "}
                        {truck.referenceId || "-"} 
                      </Typography>
                    </DetailSection>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <DetailSection title="Company">
                      <Typography variant="caption">
                        <span style={{fontWeight: "bold", color: "#4a4a4a"}}>Company:</span>{" "}
                        {truck.companyName}
                        

                      </Typography>
                                           
                                            {/* <Typography variant="caption" sx={{ display: "block" }}>
  <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
    {getRegistrationType(truck.registration_number)} :
  </span>{" "}
  {truck.registration_number ? truck.registration_number : "N/A"}
</Typography>  */}
                                           {truck.dot_number && (
  <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
      DOT Number:
    </span>{" "}
    {truck.dot_number}
  </Typography>
)}

{/* ONLY show MC Number if it actually exists in the mc_number column */}
{truck.mc_number && (
  <Typography variant="caption" sx={{ display: "block" }}>
    <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>
      MC Number:
    </span>{" "}
    {truck.mc_number.toString().replace(/^MC-?/gi, '').trim()}
  </Typography>
)}
                                          
                      <Typography variant="caption"sx={{ display: "block" }}>
                        <span style={{ fontWeight: "bold", color: "#4a4a4a"}}>State:</span>{" "}
                        {truck.companyState}
                      </Typography>

                     <Typography variant="caption" sx={{ display: "block"}}>
  <span style={{ fontWeight: "bold", color: "#4a4a4a" }}>Contact:</span>{" "}
  {truck?.contactInfo ? (
    <>
      {truck.contactInfo.includes("@") ? (
        <span>{truck.contactInfo}</span> 
      ) : (
        <span>{truck.contactInfo}</span> 
      )}
    </>
  ) : (
    <span style={{ color: "#777" }}>Not provided</span>
  )}
</Typography>

                                     </DetailSection>
                  </Box>
                  <Box sx={{ flex: 1, pr: 2 }}>
                                        <DetailSection title="Notes:">
                      <Typography variant="caption">
                        
                        {truck.comments || "No additional comments provided."}
                      </Typography>
                    </DetailSection>
                  </Box>

                </Box>
              </CardContent>
            </StyledCard>
          </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const MyTrucks = () => {
    const [formData, setFormData] = useState({
        availableDate: dayjs(),
        origin: '',
        destination: '', 
        equipment: [],
        loadType: 'Full',
        length: '',
        weight: '',
        referenceId: '',
        contactInfo: '',
        comments: '',
        originCoords: null,
        destinationCoords: null,
    });
    const [formOpen, setFormOpen] = useState(true);
    const [originOptions, setOriginOptions] = useState([]);
    const [destinationOptions, setDestinationOptions] = useState([]);
    const [originInput, setOriginInput] = useState('');
    const [destinationInput, setDestinationInput] = useState('');
    const [currentUser, setCurrentUser] = useState(null);
    const [validatingOrigin, setValidatingOrigin] = useState(false);
    const [validatingDestination, setValidatingDestination] = useState(false);
    const [equipmentTypes, setEquipmentTypes] = useState([]);
    const [repostingIds, setRepostingIds] = useState(new Set());

    const [tick, setTick] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    React.useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) setCurrentUser(JSON.parse(stored));
    }, []);

    useEffect(() => {
        const getEquipment = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const { data } = await axios.get(`${API_BASE}/api/public/equipment`, { 
                    headers: { Authorization: `Bearer ${token}` } 
                });
                setEquipmentTypes(data);
            } catch (error) {
                console.error("Could not load equipment types for dropdown.", error);
            }
        };
        getEquipment();
    }, []);

    const fetchPostedTrucks = async () => {
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
            const token = localStorage.getItem('jwtToken');
            const resp = await axios.get(`${API_BASE}/api/trucks`, { headers: { Authorization: token ? `Bearer ${token}` : '' } });
            if (Array.isArray(resp.data)) setPostedTrucks(resp.data);
            else if (resp.data.results) setPostedTrucks(resp.data.results);
        } catch (err) {
            console.error('fetchPostedTrucks error', err);
        }
    };
    const validateAndFormatLocation = async (input, fieldName, setInputState, setCoordsField) => {
        const trimmed = input?.trim();
        if (!trimmed) {
            setErrors(prev => ({ ...prev, [fieldName]: '' }));
            return true; 
        }

        if (formData[fieldName] === trimmed && formData[setCoordsField]) return true;
        
        try {
            const res = await geocodingClient.forwardGeocode({ query: trimmed, limit: 1, countries: ['US'] }).send();
            const feature = res.body.features[0];
            if (!feature) {
                setErrors(prev => ({ ...prev, [fieldName]: 'Enter a valid city, state or US zip code' }));
                return false;
            }

            let city = '';
            let stateShort = '';
            const cityContext = feature.context?.find(c => c.id.startsWith('place'));
            const stateContext = feature.context?.find(c => c.id.startsWith('region'));

          
            if (feature.id.startsWith('postcode') || (feature.text && feature.text.match(/^\d{5}$/))) {
                city = cityContext?.text;
                stateShort = stateContext?.short_code?.split('-')[1]?.toUpperCase();
            } else { 
                city = feature.text;
                stateShort = stateContext?.short_code?.split('-')[1]?.toUpperCase() || stateContext?.text;
            }
            

            if ((!city || !stateShort) && feature.place_name && feature.place_name.includes(',')) {
                 const parts = feature.place_name.split(',');
                 city = parts[0].trim();
                 if (stateContext) {
                     stateShort = stateContext.short_code?.split('-')[1]?.toUpperCase() || stateContext.text;
                 } else if (parts[1]) {
                     stateShort = parts[1].trim(); 
                 }
            }

            if (city && stateShort) {
                const formatted = `${city}, ${stateShort}`;
                setFormData(prev => ({ ...prev, [fieldName]: formatted, [setCoordsField]: feature.center }));
                setInputState(formatted); 
                setErrors(prev => ({ ...prev, [fieldName]: '' }));
                return true;
            } else {
                setErrors(prev => ({ ...prev, [fieldName]: 'Enter a valid city, state or US zip code' }));
                return false;
            }
        } catch (err) {
            console.error("Geocoding validation error:", err);
            setErrors(prev => ({ ...prev, [fieldName]: 'Location validation failed' }));
            return false;
        }
    };

    React.useEffect(() => {
        fetchPostedTrucks();
    }, []);

  
    const REPOST_COOLDOWNS_TRUCKS_KEY = 'repostCooldowns_trucks';

    const [repostCooldowns, setRepostCooldowns] = useState(() => {
        try {
            const stored = localStorage.getItem(REPOST_COOLDOWNS_TRUCKS_KEY);
            if (!stored) return {};

            const parsed = JSON.parse(stored);
            const now = dayjs();
            const cooldownMinutes = 15;
            const cleaned = {};

            for (const id in parsed) {
              
                if (now.diff(dayjs(parsed[id])) < cooldownMinutes * 60 * 1000) {
                    cleaned[id] = parsed[id];
                }
            }
            return cleaned;
        } catch (e) {
            console.error("Failed to read or parse repost cooldowns from localStorage", e);
            return {};
        }
    });

    
    useEffect(() => {
        try {
            localStorage.setItem(REPOST_COOLDOWNS_TRUCKS_KEY, JSON.stringify(repostCooldowns));
        } catch (e) {
            console.error("Failed to save repost cooldowns to localStorage", e);
        }
    }, [repostCooldowns]);

    
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {}, 
    });

 
    const handleRepost = async (id) => {
        const now = dayjs();
        const lastRepost = repostCooldowns[id] ? dayjs(repostCooldowns[id]) : null;
        const cooldownMinutes = 15;
        const cooldownDuration = cooldownMinutes * 60 * 1000;

        if (lastRepost && now.diff(lastRepost) < cooldownDuration) {
            const timeLeft = dayjs.duration(cooldownDuration - now.diff(lastRepost));
            setSubmissionStatus({ open: true, message: `Repost will be allowed after ${timeLeft.minutes()}m ${timeLeft.seconds()}s. ⏳`, severity: 'warning' });
            return;
        }

        setRepostingIds(prev => new Set(prev).add(id));

        try {
            const token = localStorage.getItem('jwtToken');
            const truck = postedTrucks.find(t => t.id === id);
            if (!truck) return;

           const copyPayload = { ...truck };
delete copyPayload.id;
delete copyPayload.timePosted;
delete copyPayload.postedTime;
delete copyPayload.deadheadOrigin;
delete copyPayload.deadheadDestination;
delete copyPayload.isActive;

copyPayload.availableDate = dayjs().format('YYYY-MM-DD');


// if (copyPayload.availableDate && copyPayload.availableDate.toISOString) {
//     copyPayload.availableDate = copyPayload.availableDate.toISOString();
// }

            const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
            const res = await fetch(`${API_BASE}/api/trucks`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
                body: JSON.stringify(copyPayload),
            });
            if (!res.ok) {
                let errorDetails = 'Unknown server error.';
                try { const errJson = await res.json(); errorDetails = errJson.message || JSON.stringify(errJson); } catch(e) { errorDetails = await res.text(); }
                console.error('Repost failed with status:', res.status, 'Error Body:', errorDetails);
                throw new Error('Repost failed: ' + errorDetails);
            }
            // AFTER (fixed)
            const created = await res.json();

            // Preserve the exact "City, ST" format from the original truck.
            // The server may reformat using full Mapbox place_name ("Houston, United States, Texas")
            // when it geocodes from the stored coordinates — override it here.
            const displayTruck = {
                ...created,
                origin: truck.origin,
                destination: truck.destination,
            };

            setPostedTrucks(prev => [displayTruck, ...prev]);
            setRepostCooldowns(prev => ({ ...prev, [id]: now.toISOString(), [created.id]: now.toISOString() }));
            const channel = new BroadcastChannel('app_data_sync');
            channel.postMessage('DATA_CHANGED');
            channel.close();
            setSubmissionStatus({ open: true, message: 'Truck reposted successfully! ✅', severity: 'success' });
            // Removed fetchPostedTrucks() — it was re-fetching server data and overwriting the correct format above
        } catch (err) {
            console.error('repost error', err);
            setSubmissionStatus({ open: true, message: 'Failed to repost truck', severity: 'error' });
        } finally {
            setRepostingIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
        }
    };


    const handleToggleStatus = async (truckId) => {
        try {
            const token = localStorage.getItem('jwtToken');
            const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
            const res = await fetch(`${API_BASE}/api/trucks/${truckId}/status`, {
                method: 'PATCH',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {},
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || 'Failed to update truck status');
            }
            const updated = await res.json();
            setPostedTrucks(prev => prev.map(t => t.id === truckId ? updated : t));
            setSubmissionStatus({ open: true, message: `Truck is now ${updated.isActive ? 'Active' : 'Deactivated'}.`, severity: 'info' });
            const channel = new BroadcastChannel('app_data_sync');
            channel.postMessage('DATA_CHANGED');
            channel.close();
        } catch (err) {
            console.error('toggle truck status error', err);
            setSubmissionStatus({ open: true, message: 'Could not update truck status', severity: 'error' });
        }
    };

    const handleToggleStatusWithConfirm = (truckId, isActive) => {
        const action = isActive ? 'DEACTIVATE' : 'ACTIVATE';
        setConfirmDialog({
            open: true,
            title: `Confirm ${action}`,
            message: `Are you sure you want to ${action.toLowerCase()} this truck post?`,
            onConfirm: () => {
              
                handleToggleStatus(truckId); 
                setConfirmDialog({ ...confirmDialog, open: false }); 
            }
        });
    };

    const [errors, setErrors] = useState({});
    const [postedTrucks, setPostedTrucks] = useState([]);
    const [submissionStatus, setSubmissionStatus] = useState({ open: false, message: '', severity: 'success' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const validateForm = async () => {
        let newErrors = {};

        const originVal = originInput.trim();
        const destinationVal = destinationInput.trim();
        const contactVal = formData.contactInfo.trim();
        const lengthVal = Number(formData.length);
        const weightVal = Number(formData.weight);
        const refIdVal = formData.referenceId;

        if (!originVal) newErrors.origin = "Origin is required";
       
        if (originVal) {
            setValidatingOrigin(true);
            const valid = await validateAndFormatLocation(originVal, 'origin', setOriginInput, 'originCoords');
            setValidatingOrigin(false);
            if (!valid) newErrors.origin = errors.origin || 'Enter a valid city, state or US zip code';
        }
        if (destinationVal) {
            setValidatingDestination(true);
            const valid = await validateAndFormatLocation(destinationVal, 'destination', setDestinationInput, 'destinationCoords');
            setValidatingDestination(false);
            if (!valid) newErrors.destination = errors.destination || 'Enter a valid city, state or US zip code';
        }

        if (!formData.equipment) newErrors.equipment = "Equipment is required";
        if (!formData.equipment || formData.equipment.length === 0) newErrors.equipment = "Equipment is required";
        if (!formData.loadType) newErrors.loadType = "Load type is required";
        if (!contactVal) newErrors.contactInfo = "Contact info is required";
        if (!formData.availableDate) newErrors.availableDate = "Date is required";


    if (!formData.length) newErrors.length = "Length is required";
        if (formData.length !== '' && (lengthVal < 0 || lengthVal > 999)) {
            newErrors.length = "Length must be between 0–999 ft";
        }
        
        if (!formData.weight) newErrors.weight = "Weight is required";
        if (formData.weight !== '' && (weightVal < 0 || weightVal > 99999999)) {
            newErrors.weight = "Weight must be between 0–99,999,999 lbs";
        }
        
       
        if (refIdVal && refIdVal.length > 20) {
            newErrors.referenceId = "Max 20 characters allowed";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

   const handleChange = (e) => {
    const { name, value } = e.target;


    if (value === '') {
        setFormData(prev => ({ ...prev, [name]: value }));
        return;
    }

    const numValue = parseFloat(value);

    if (name === 'length') {
       
        if (numValue === 0 && value.length > 1 && !value.includes('.')) {
            return; 
        }

        if (numValue >= 0 && numValue <= 999) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        return;
    }

    if (name === 'weight') {
       
        if (numValue === 0 && value.length > 1 && !value.includes('.')) {
            return; 
        }

  
        if (numValue >= 0 && numValue <= 99999999) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        return;
    }

 
    setFormData(prev => ({ ...prev, [name]: value }));
};

    const handleDateChange = (date) => setFormData(prev => ({ ...prev, availableDate: date }));
    const handleClear = () => {
        setFormData({
            availableDate: dayjs(), origin: '', destination: '', equipment: '',
            loadType: 'Full', length: '', weight: '', referenceId: '',
            contactInfo: '', comments: '', originCoords: null, destinationCoords: null
        });
        setOriginInput(''); 
        setDestinationInput('');
        setErrors({});
    };
    const handleCloseSnackbar = () => setSubmissionStatus({ ...submissionStatus, open: false });


const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    const isValid = await validateForm();
    if (!isValid) {
        setSubmissionStatus({
            open: true,
            message: "Please fix the errors before submitting.",
            severity: 'error'
        });
        setIsSubmitting(false);
        return;
    }

    try {
        const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
        const token = localStorage.getItem('jwtToken');

     
        const selectedContactInfo = formData.contactInfo || null;

   
        const payload = {
            origin: formData.origin,
            destination: formData.destination,
            availableDate: formData.availableDate ? formData.availableDate.format('YYYY-MM-DD') : null,
            truckType: formData.equipment, 
            length: formData.length || null,
            weight: formData.weight || null,
            fullOrPartial: formData.loadType,
            comments: formData.comments || null,
            originCoords: formData.originCoords || null,
            destinationCoords: formData.destinationCoords || null,
            contactInfo: selectedContactInfo || null,
            referenceId: formData.referenceId || null,
        };

        let resp;

      
        if (formData.id) {
            resp = await axios.put(`${API_BASE}/api/trucks/${formData.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const updatedTruck = resp.data;
           
            setPostedTrucks(prev =>
                prev.map(truck =>
                    truck.id === updatedTruck.id ? updatedTruck : truck
                )
            );
            setSubmissionStatus({
                open: true,
                message: "Truck updated successfully!",
                severity: 'success'
            });
            const channel = new BroadcastChannel('app_data_sync');
            channel.postMessage('DATA_CHANGED');
            channel.close();
        } else {
   
            resp = await axios.post(`${API_BASE}/api/trucks`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const created = resp.data;
            setPostedTrucks(prev => [created, ...prev]);

            const now = dayjs();
            setRepostCooldowns(prev => ({
                ...prev,
                [created.id]: now.toISOString(),
            }));

            setSubmissionStatus({
                open: true,
                message: "Truck posted successfully! ✅ You can repost after 15 minutes.",
                severity: 'success'
            });
            const channel = new BroadcastChannel('app_data_sync');
            channel.postMessage('DATA_CHANGED');
            channel.close();
        }

   
        handleClear();

    } catch (err) {
        console.error('Post/Update truck error:', err);
        setSubmissionStatus({
            open: true,
            message: 'Failed to post or update truck. Please try again later.',
            severity: 'error'
        });
    } finally {
        setIsSubmitting(false);
    }
};

const handleEdit = (id) => {
    setConfirmDialog({
        open: true,
        title: 'Confirm Edit',
        message:
            'Do you want to move this truck to the "Post a Truck" form for editing? It will not create a duplicate post.',
        onConfirm: () => {
            const truck = postedTrucks.find(t => t.id === id || t._id === id);
            if (truck) {
     
                const formattedDate = truck.availableDate 
                    ? dayjs(truck.availableDate).startOf('day') 
                    : dayjs().startOf('day');

                // Parse equipment: could be string like "Reefer,Van" or array
                let equipmentList = [];
                if (truck.equipment) {
                    if (Array.isArray(truck.equipment)) {
                        equipmentList = truck.equipment;
                    } else if (typeof truck.equipment === 'string') {
                        equipmentList = truck.equipment.split(',').map(e => e.trim());
                    }
                }
                
                // Get loadType from fullOrPartial or load type field
                const loadType = truck.fullOrPartial || truck.loadType || 'Full';

                setFormData({
                    ...truck,
                    id: truck.id || truck._id,
                    equipment: equipmentList,
                    loadType: loadType,
                    availableDate: formattedDate,
                    length: truck.length || '',
                    weight: truck.weight || '',
                    referenceId: truck.referenceId || '',
                    contactInfo: truck.contactInfo || '',
                    comments: truck.comments || '',
                    originCoords: truck.originCoords || null,
                    destinationCoords: truck.destinationCoords || null,
                });
                setFormOpen(true);

                setOriginInput(truck.origin || '');
                setDestinationInput(truck.destination || '');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            setConfirmDialog({ ...confirmDialog, open: false });
        },
    });
};

const handleDelete = (id) => {
    setConfirmDialog({
        open: true,
        title: 'Confirm Deletion',
        message: 'Are you sure you want to delete this truck post? This action cannot be undone.',
        onConfirm: async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
                const token = localStorage.getItem('jwtToken');

                await axios.delete(`${API_BASE}/api/trucks/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

    
                setPostedTrucks(postedTrucks.filter(t => t.id !== id && t._id !== id));
                setSubmissionStatus({
                    open: true,
                    message: "Truck deleted successfully!",
                    severity: 'warning'
                });
                const channel = new BroadcastChannel('app_data_sync');
                channel.postMessage('DATA_CHANGED');
                channel.close();
            } catch (err) {
                console.error('Delete failed', err);
                setSubmissionStatus({
                    open: true,
                    message: "Failed to delete truck. Please try again.",
                    severity: 'error'
                });
            } finally {
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        },
    });
};



const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    if (!option.place_name) return '';
    const city = option.text || "";
    const state = option.context?.find(c => c.id.startsWith("region"));
    const stateShort = state?.short_code?.split('-')[1]?.toUpperCase() || state?.text || "";
    return city && stateShort ? `${city}, ${stateShort}` : option.place_name;
};

    const handleCopy = (id) => {
        const truck = postedTrucks.find(t => t.id === id);
        if (truck) {
            const copy = { ...truck, id: Date.now(), timePosted: new Date() };
            setPostedTrucks(prev => [copy, ...prev]);
            setSubmissionStatus({ open: true, message: "Truck copied successfully!", severity: 'info' });
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Container maxWidth="xl" sx={{ mt: 0, mb: 1 }}>
              
                <Dialog
                    open={confirmDialog.open}
                    onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
                    aria-labelledby="confirm-dialog-title"
                    aria-describedby="confirm-dialog-description"
                   
                    maxWidth="xs" 
                    fullWidth 
                >
                    <DialogTitle 
                        id="confirm-dialog-title"
                       
                        sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#11224d' }}
                    >
                        {confirmDialog.title}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText 
                            id="confirm-dialog-description"
                         
                            sx={{ fontSize: '0.9rem' }}
                        >
                            {confirmDialog.message}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{ padding: '8px 16px' }}>
                      
                        <Button 
                            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
                            size="small" 
                            sx={{
                                fontSize: '0.65rem', 
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                borderRadius: 2,
                                padding: '6px 16px',
                                border: `1px solid ${theme.palette.primary.main}`,
                                color: theme.palette.primary.main,
                                backgroundColor: 'transparent',
                                '&:hover': { backgroundColor: 'rgba(17, 34, 77, 0.05)' },
                            }}
                        >
                            Cancel
                        </Button>
                      
                        <Button 
                            onClick={confirmDialog.onConfirm} 
                            autoFocus 
                            size="small"
                        
                            sx={{
                                fontSize: '0.65rem', 
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                borderRadius: 2,
                                padding: '6px 16px',
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                color: 'white',
                                '&:hover': {
                                    background: `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
                                },
                            }}
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            
                <StyledPaper>
                    {!formOpen && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                            onClick={() => setFormOpen(true)}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#11224d' }}>
                                Post a Truck
                            </Typography>
                            <Tooltip title="Expand Form">
                                <IconButton size="small">
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                        <KeyboardArrowDownIcon sx={{ fontSize: '1.2rem',mt: -1.5, }} />
                                    </Box>
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <Collapse in={formOpen}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(200px, 1fr))' },
                            gap: 1.5,
                            alignItems: 'start',
                        }}
                    >
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="Available date(s) "
                                    value={formData.availableDate}
                                    onChange={handleDateChange}
                                    minDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            size: 'small',
                                            required: true,
                                            error: !!errors.availableDate
                                        }
                                    }}
                                />
                            </LocalizationProvider>
                            {errors.availableDate && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.availableDate}</Typography>}
                        </Box>

                        <Box>
                            <Autocomplete
                                freeSolo
                                size="small"
                                options={originOptions}
                                inputValue={originInput}
                                getOptionLabel={getOptionLabel}
                                onInputChange={async (e, value) => {
                                    setOriginInput(value || '');
                                    setFormData(prev => ({ ...prev, origin: value || '' }));
                                    if (!value) {
                                        setOriginOptions([]);
                                        return;
                                    }
                                    const suggestions = await fetchMapboxSuggestions(value);
                                    setOriginOptions(suggestions);
                                }}
                                onChange={async (e, selectedOption) => {
                                    if (!selectedOption) return;
                                    const formatted = getOptionLabel(selectedOption);
                                    setOriginInput(formatted);
                                    setFormData(prev => ({ ...prev, origin: formatted, originCoords: selectedOption.center || null }));
                                    await validateAndFormatLocation(formatted, 'origin', setOriginInput, 'originCoords');
                                }}
                                renderInput={(params) => (
                                    <CompactTextField
                                        {...params}
                                        fullWidth
                                        label="Origin (City, ST, ZIP)"
                                        name="origin"
                                        required
                                        error={!!errors.origin}
                                        helperText={errors.origin}
                                        onBlur={async () => {
                                            setValidatingOrigin(true);
                                            await validateAndFormatLocation(originInput, 'origin', setOriginInput, 'originCoords');
                                            setValidatingOrigin(false);
                                        }}
                                    />
                                )}
                            />
                        </Box>


<Box>
  <Autocomplete
    freeSolo
    size="small"
    options={destinationOptions}
    inputValue={destinationInput}
    getOptionLabel={(option) => {
      if (typeof option === "string") return option;
      if (!option) return "";

      const city = option.text || "";
      const state = option.context?.find((c) => c.id.startsWith("region"));
      const stateShort =
        state?.short_code?.split("-")[1]?.toUpperCase() || state?.text || "";

      return city && stateShort ? `${city}, ${stateShort}` : option.place_name || city;
    }}
    onInputChange={async (e, value) => {
      setDestinationInput(value || "");
      setFormData((prev) => ({ ...prev, destination: value || "" }));
      if (!value) {
        setDestinationOptions([]);
        return;
      }
      const suggestions = await fetchMapboxSuggestions(value);
      setDestinationOptions(suggestions);
    }}
    onChange={async (e, selectedOption) => {
      if (!selectedOption) return;

      let city = "";
      let stateShort = "";
      let coords = null;

      try {
        if (
          typeof selectedOption === "object" &&
          selectedOption.place_type?.includes("postcode")
        ) {
       
          const zipCode = selectedOption.text;
          const res = await geocodingClient
            .forwardGeocode({
              query: zipCode,
              limit: 1,
              countries: ["US"],
            })
            .send();

          const feature = res.body.features[0];
          if (feature) {
            const placeCtx = feature.context?.find((c) =>
              c.id.startsWith("place")
            );
            const regionCtx = feature.context?.find((c) =>
              c.id.startsWith("region")
            );

            city =
              placeCtx?.text ||
              feature.text ||
              feature.properties?.context?.place?.name ||
              "";
            stateShort =
              regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
            coords = feature.center || null;
          }
        } else if (typeof selectedOption === "object") {
          const regionCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("region")
          );
          const placeCtx = selectedOption.context?.find((c) =>
            c.id.startsWith("place")
          );

          city = selectedOption.text || placeCtx?.text || "";
          stateShort =
            regionCtx?.short_code?.split("-")[1]?.toUpperCase() || "";
          coords = selectedOption.center || null;
        } else {
          city = selectedOption;
        }

        const formatted =
          city && stateShort ? `${city}, ${stateShort}` : city || stateShort;

    
        setDestinationInput(formatted);
        setFormData((prev) => ({
          ...prev,
          destination: formatted,
          destinationCoords: coords,
        }));

    
        await validateAndFormatLocation(
          formatted,
          setFormData,
          setErrors,
          "destination",
          (val) =>
            setFormData((prev) => ({
              ...prev,
              destination: val,
              destinationCoords: coords,
            }))
        );
      } catch (err) {
        console.error("MyTruck Destination ZIP conversion error:", err);
      }
    }}
    renderInput={(params) => (
      <CompactTextField
        {...params}
        fullWidth
        label="Destination (City, ST, ZIP)"
        name="destination"
        required
        value={destinationInput}
        error={!!errors.destination}
        helperText={errors.destination}
        onBlur={async () => { 
          setValidatingDestination(true);
          await validateAndFormatLocation(destinationInput, 'destination', setDestinationInput, 'destinationCoords');
          setValidatingDestination(false);
        }}
      />
    )}
  />
</Box>

                        <Box>
                            <Autocomplete
                                multiple
                                limitTags={1}
                                disableCloseOnSelect
                                size="small"
                                id="equipment-type-select"
                                options={equipmentTypes}
                                getOptionLabel={(option) => option.label}
                                value={equipmentTypes.filter(e => formData.equipment.includes(e.label))}
                                onChange={(event, newValue) => {
                                    setFormData({ ...formData, equipment: newValue.map(option => option.label) });
                                }}
                                sx={{
                                    background: "#fff",
                                    "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap !important" },
                                    "& .MuiChip-root": { maxHeight: 24, fontSize: 12 }
                                }}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                                        {`${option.label} (${option.short})`}
                                    </li>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            variant="outlined"
                                            label={option.short}
                                            size="small"
                                            {...getTagProps({ index })}
                                        />
                                    ))
                                }
                                renderInput={(params) => (
                                    <CompactTextField
                                        {...params}
                                        required
                                        label="Equipment Type"
                                        placeholder={formData.equipment.length > 0 ? "" : "Any"}
                                        error={!!errors.equipment}
                                        helperText={errors.equipment}
                                    />
                                )}
                            />
                        </Box>

                        <Box>
                            <CompactFormControl fullWidth variant="outlined" size="small" required error={!!errors.loadType}>
                                <InputLabel>Load Type</InputLabel>
                                <CompactSelect name="loadType" value={formData.loadType} onChange={handleChange} label="Load Type">
                                    <MenuItem value="Full">Full</MenuItem>
                                    <MenuItem value="Partial">Partial</MenuItem>
                                   <MenuItem value="Full or Partial">Full or Partial</MenuItem>
                                </CompactSelect>
                            </CompactFormControl>
                            {errors.loadType && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.loadType}</Typography>}
                        </Box>

                        <Box>
                            <CompactTextField
                                required fullWidth label="Feet" name="length" type="number"
                                value={formData.length} onChange={handleChange}
                                error={!!errors.length} 
                                inputProps={{ min: 0, max: 999 }}
                            />
                            {errors.length && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.length}</Typography>}
                        </Box>

                        <Box>
                            <CompactTextField
                                required fullWidth label="Weight (lbs.) " name="weight" type="number"
                                value={formData.weight} onChange={handleChange}
                                error={!!errors.weight} 
                                inputProps={{ min: 0, max: 99999999 }}
                            />
                            {errors.weight && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.weight}</Typography>}
                        </Box>

                        <Box>
                            <CompactTextField
                                fullWidth label="Reference ID" name="referenceId"
                                value={formData.referenceId} onChange={handleChange}
                                error={!!errors.referenceId}
                                inputProps={{ maxLength: 20 }}
                            />
                            {errors.referenceId && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.referenceId}</Typography>}
                        </Box>

                       

                        <Box>
                            <CompactTextField
                             required
                                select
                                fullWidth
                                label="Contact Info"
                                name="contactInfo"
                                value={formData.contactInfo}
                                onChange={handleChange}
                                error={!!errors.contactInfo}
                            >
                                {currentUser?.phone_number && (
                                    <MenuItem value={currentUser.phone_number}>{currentUser.phone_number}</MenuItem>
                                )}
                                {currentUser?.email && (
                                    <MenuItem value={currentUser.email}>{currentUser.email}</MenuItem>
                                )}
                            </CompactTextField>
                            {errors.contactInfo && <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block' }}>{errors.contactInfo}</Typography>}
                        </Box>

                        <Box>
                            <CompactTextField
                                fullWidth label="Notes" name="comments"
                                value={formData.comments} onChange={handleChange}
                            />
                           
                        </Box>
                        <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0.5, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                        <Tooltip title="Collapse Form">
                            <IconButton size="small" onClick={() => setFormOpen(false)} sx={{ color: 'primary.main', mr: 0.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', color: 'grey.500' }} />
                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem', mt: -1.5, color: 'grey.500' }} />
                                    <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem',mt: -1.5, }} />
                                </Box>
                            </IconButton>
                        </Tooltip>
                        <ClearButton variant="outlined" onClick={handleClear} startIcon={<ClearIcon />} size="small">
                            Clear
                        </ClearButton>
                        <PostButton 
                            variant="contained" 
                            onClick={handleSubmit} 
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <PostAddIcon />} 
                            size="small"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Posting...' : 'Post'}
                        </PostButton>
                    </Box>
                    </Box>
                    </Collapse>

                    
                </StyledPaper>

          
                {postedTrucks.length === 0 && (
                    <Box sx={{ textAlign: 'center', mt: 4, mb: 4 }}>
                        <LocalShippingIcon sx={{ fontSize: 80, color: theme.palette.primary.main, mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Post your trucks to find matching loads.
                        </Typography>
                    </Box>
                )}

             
{postedTrucks.length > 0 && (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
      Posted Trucks
    </Typography>
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead sx={{ backgroundColor: theme.palette.primary.main, position: 'sticky', top: 0, zIndex: 10 }}>
          <TableRow>
            {["Posted", "Date", "Origin", "Destination", "Equip", "Base", "Status", "Actions"].map((col, index) => (
              <TableCell
                key={col}
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  py: 0.5,
                  textAlign: 'center' ,
                  whiteSpace: 'nowrap',
                  borderBottom: 'none'
                }}
              >
                {col}
                {index === 0 && <TableProgressBar sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', zIndex: 5 }} />}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {postedTrucks
          .filter(truck => {
    const today = dayjs().startOf('day'); 
    const availableDate = truck.availableDate ? dayjs(truck.availableDate).startOf('day') : null; 
    return availableDate && (availableDate.isSame(today, 'day') || availableDate.isAfter(today));
})
            .map((truck, idx) => (
            <TruckRow
              key={truck.id}
    truck={truck}
    idx={idx}
    handleEdit={handleEdit}
    handleDelete={handleDelete}
    handleCopy={handleCopy}
    handleRepost={handleRepost}

    handleToggleStatus={handleToggleStatusWithConfirm}
    equipmentTypes={equipmentTypes}
    repostingIds={repostingIds}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
)}

                <Snackbar
                    open={submissionStatus.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={submissionStatus.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {submissionStatus.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default MyTrucks;